import { supabase } from '../supabase/supabaseClient';
import patientService from './PatientService';

/**
 * Simple Analytics Service
 * Tracks user sessions, game events, and dashboard interactions
 * Designed to work with localStorage-based authentication
 */
class AnalyticsService {
  constructor() {
    this.currentSessionId = null;
    this.sessionStartTime = null;
    this.eventBuffer = [];
    this.isProcessing = false;
    this.maxBufferSize = 50;
    this.flushInterval = 30000; // 30 seconds
    
    // Check if Supabase is configured
    this.isEnabled = this.checkSupabaseConfig();
    
    if (this.isEnabled) {
      // Start periodic flush
      this.startPeriodicFlush();
      console.log('📊 Analytics Service initialized and ready');
    } else {
      console.warn('⚠️ Analytics Service disabled - Supabase not configured');
    }
    
    // Bind methods to preserve context
    this.trackUserLogin = this.trackUserLogin.bind(this);
    this.trackUserLogout = this.trackUserLogout.bind(this);
    this.trackGameStart = this.trackGameStart.bind(this);
    this.trackGameComplete = this.trackGameComplete.bind(this);
    this.trackGameAbandon = this.trackGameAbandon.bind(this);
    this.trackDashboardEvent = this.trackDashboardEvent.bind(this);
  }

  /**
   * Check if Supabase is properly configured
   */
  checkSupabaseConfig() {
    try {
      return !!(process.env.REACT_APP_SUPABASE_URL && process.env.REACT_APP_SUPABASE_KEY);
    } catch (error) {
      console.warn('Analytics: Supabase config check failed', error);
      return false;
    }
  }

  /**
   * Start periodic event flushing
   */
  startPeriodicFlush() {
    setInterval(() => {
      this.flushEvents();
    }, this.flushInterval);
  }

  /**
   * Track user login and create new session
   */
  async trackUserLogin(userName) {
    console.log('📊 trackUserLogin called for:', userName, 'Enabled:', this.isEnabled);

    if (!this.isEnabled || !userName) return;

    try {
      this.sessionStartTime = new Date();

      const { data, error } = await supabase
        .from('user_sessions')
        .insert([{
          user_name: userName,
          session_start: this.sessionStartTime.toISOString(),
          login_method: 'simple_auth'
        }])
        .select('id')
        .single();

      if (error) throw error;

      this.currentSessionId = data.id;
      console.log('📊 User login tracked, session created:', userName, 'Session ID:', data.id);

      return data.id;
    } catch (error) {
      console.error('Analytics: Failed to track login', error);
      return null;
    }
  }

  /**
   * Track user logout and end session
   */
  async trackUserLogout(userName, useBeacon = false) {
    if (!this.isEnabled || !this.currentSessionId) return;

    try {
      const sessionEnd = new Date();
      const sessionDuration = Math.floor((sessionEnd - this.sessionStartTime) / 1000);

      // For page unload scenarios, use sendBeacon for reliability
      if (useBeacon && navigator.sendBeacon) {
        const updateData = {
          session_end: sessionEnd.toISOString(),
          session_duration_seconds: sessionDuration
        };
        
        // Create a simple endpoint call using sendBeacon
        const beaconData = JSON.stringify({
          table: 'user_sessions',
          action: 'update',
          id: this.currentSessionId,
          data: updateData
        });
        
        // Try to send via beacon (more reliable during page unload)
        const beaconSent = navigator.sendBeacon(
          `${process.env.REACT_APP_SUPABASE_URL}/rest/v1/user_sessions?id=eq.${this.currentSessionId}`,
          new Blob([JSON.stringify(updateData)], { type: 'application/json' })
        );
        
        if (beaconSent) {
          console.log('📊 User logout tracked via beacon:', userName, `(${sessionDuration}s)`);
          // Reset session immediately for beacon calls
          this.currentSessionId = null;
          this.sessionStartTime = null;
          return;
        }
      }

      // Standard async approach for normal logout
      const { error } = await supabase
        .from('user_sessions')
        .update({
          session_end: sessionEnd.toISOString(),
          session_duration_seconds: sessionDuration
        })
        .eq('id', this.currentSessionId);

      if (error) throw error;

      console.log('📊 User logout tracked:', userName, `(${sessionDuration}s)`);
      
      // Flush any remaining events before ending session
      await this.flushEvents();
      
      // Reset session
      this.currentSessionId = null;
      this.sessionStartTime = null;
      
    } catch (error) {
      console.error('Analytics: Failed to track logout', error);
      // Even if tracking fails, reset session to prevent stale state
      this.currentSessionId = null;
      this.sessionStartTime = null;
    }
  }

  /**
   * Track game start
   */
  trackGameStart(userName, gameName) {
    console.log('📊 trackGameStart called:', { userName, gameName, isEnabled: this.isEnabled, sessionId: this.currentSessionId });

    if (!this.isEnabled) {
      console.warn('📊 Analytics disabled - Supabase not configured');
      return;
    }

    const event = {
      session_id: this.currentSessionId,
      user_name: userName,
      event_type: 'game_started',
      game_name: gameName,
      completion_status: 'in_progress',
      timestamp: new Date().toISOString()
    };

    this.addEventToBuffer(event, 'game_events');
    console.log('📊 Game start tracked:', gameName, 'Session:', this.currentSessionId);
  }

  /**
   * Track game completion
   */
  trackGameComplete(userName, gameName, durationSeconds = null, metadata = {}) {
    console.log('📊 trackGameComplete called:', {
      userName,
      gameName,
      durationSeconds,
      metadata,
      isEnabled: this.isEnabled,
      sessionId: this.currentSessionId
    });

    if (!this.isEnabled) {
      console.warn('📊 Analytics disabled - tracking in localStorage only');
      // Still track detailed metrics which will use localStorage
      this.trackDetailedGameMetrics(userName, gameName, durationSeconds, metadata);
      return;
    }

    // Extract reps from metadata for numeric column
    const repsCompleted = metadata.reps_completed || metadata.reps || 0;

    const event = {
      session_id: this.currentSessionId,
      user_name: userName,
      event_type: 'game_completed',
      game_name: gameName,
      game_duration_seconds: durationSeconds,
      completion_status: 'completed',
      reps_completed: repsCompleted,  // Store as numeric column
      metadata: metadata,
      timestamp: new Date().toISOString()
    };

    this.addEventToBuffer(event, 'game_events');
    console.log('📊 Game completion tracked:', gameName, durationSeconds ? `(${durationSeconds}s)` : '', `- ${repsCompleted} reps`, 'Event:', event);

    // Always track detailed analytics for game_analytics table
    this.trackDetailedGameMetrics(userName, gameName, durationSeconds, metadata);
  }

  /**
   * Track game abandonment
   */
  trackGameAbandon(userName, gameName, durationSeconds = null, metadata = {}) {
    if (!this.isEnabled) {
      console.warn('📊 Analytics disabled - tracking abandonment in localStorage only');
      // Still track detailed metrics which will use localStorage
      this.trackDetailedGameMetrics(userName, gameName, durationSeconds, {
        ...metadata,
        completionStatus: 'abandoned',
        formScore: 0,
        rom: 0,
        accuracyScore: 0,
        controlScore: 0
      });
      return;
    }

    // Extract reps from metadata for numeric column (same as completion)
    const repsCompleted = metadata.reps_completed || metadata.reps || 0;

    const event = {
      session_id: this.currentSessionId,
      user_name: userName,
      event_type: 'game_abandoned',
      game_name: gameName,
      game_duration_seconds: durationSeconds,
      completion_status: 'abandoned',
      reps_completed: repsCompleted,  // Store reps even for abandoned games
      metadata: metadata,
      timestamp: new Date().toISOString()
    };

    this.addEventToBuffer(event, 'game_events');
    console.log('📊 Game abandonment tracked:', gameName, durationSeconds ? `(${durationSeconds}s)` : '', `- ${repsCompleted} reps`);

    // Also track in game_analytics for comprehensive reporting
    this.trackDetailedGameMetrics(userName, gameName, durationSeconds, {
      ...metadata,
      completionStatus: 'abandoned',
      formScore: 0, // No form score for abandoned games
      rom: 0, // No ROM for abandoned games
      accuracyScore: 0,
      controlScore: 0
    });
  }

  /**
   * Track dashboard events (navigation, feature usage)
   */
  trackDashboardEvent(userName, eventType, pageOrAction = null, metadata = {}) {
    if (!this.isEnabled) return;

    const event = {
      session_id: this.currentSessionId,
      user_name: userName,
      event_type: eventType,
      page_or_action: pageOrAction,
      metadata: metadata,
      timestamp: new Date().toISOString()
    };

    this.addEventToBuffer(event, 'dashboard_events');
    console.log('📊 Dashboard event tracked:', eventType, pageOrAction);
  }

  /**
   * Add event to buffer for batch processing
   */
  addEventToBuffer(event, table) {
    this.eventBuffer.push({ event, table });
    
    // Flush if buffer is getting full
    if (this.eventBuffer.length >= this.maxBufferSize) {
      this.flushEvents();
    }
  }

  /**
   * Flush events from buffer to database
   */
  async flushEvents() {
    // Only log when there are actual events to process
    if (this.eventBuffer.length > 0) {
      console.log('📊 Flushing', this.eventBuffer.length, 'events to database');
      // Log summary of what's being flushed
      const gameAnalyticsEvents = this.eventBuffer.filter(e => e.table === 'game_analytics');
      if (gameAnalyticsEvents.length > 0) {
        console.log('📊 Game analytics events to flush:', gameAnalyticsEvents.map(e => ({
          game: e.event.game_name,
          patient_id: e.event.patient_id,
          reps: e.event.reps_completed,
          form_score: e.event.form_score
        })));
      }
    }

    if (!this.isEnabled || this.isProcessing || this.eventBuffer.length === 0) {
      // Only warn about disabled analytics once
      if (!this.isEnabled && this.eventBuffer.length > 0) {
        console.warn('📊 Analytics is disabled - events will not be saved to database');
      }
      return;
    }

    this.isProcessing = true;
    const eventsToProcess = [...this.eventBuffer];
    this.eventBuffer = [];

    try {
      // Group events by table
      const eventsByTable = eventsToProcess.reduce((acc, { event, table }) => {
        if (!acc[table]) acc[table] = [];
        acc[table].push(event);
        return acc;
      }, {});

      console.log('📊 Events grouped by table:', eventsByTable);

      // Insert events for each table
      for (const [table, events] of Object.entries(eventsByTable)) {
        console.log(`📊 Inserting ${events.length} events to ${table}:`, events);

        const { data, error } = await supabase
          .from(table)
          .insert(events)
          .select();

        if (error) {
          console.error(`Analytics: Failed to insert ${table} events`, error);
          // Put failed events back in buffer
          events.forEach(event => this.eventBuffer.push({ event, table }));
        } else {
          console.log(`📊 Successfully flushed ${events.length} events to ${table}`);
          if (table === 'game_analytics' && data) {
            console.log('📊 Inserted game_analytics records:', data.map(d => ({
              patient_id: d.patient_id,
              game_name: d.game_name,
              start_time: d.start_time,
              form_score: d.form_score,
              reps_completed: d.reps_completed
            })));
          }
        }
      }
    } catch (error) {
      console.error('Analytics: Failed to flush events', error);
      // Put all events back in buffer on error
      eventsToProcess.forEach(item => this.eventBuffer.push(item));
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Track detailed game metrics (new enhanced analytics)
   */
  trackDetailedGameMetrics(userName, gameName, durationSeconds = null, metadata = {}) {
    // This method handles both database and localStorage storage

    // Extract patient and clinician IDs from localStorage
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    // Patient ID is stored separately in localStorage for patients
    const storedPatientId = localStorage.getItem('patientId');
    
    // CRITICAL: Only use patient_id from metadata or localStorage
    // Never fall back to user.id as they are different entities
    const patientId = metadata.patient_id || storedPatientId;
    
    if (!patientId) {
      console.error('📊 Cannot track analytics - no patient ID available. Metadata:', metadata, 'Stored:', storedPatientId);
      // Store in localStorage as fallback for mock/testing
      if (userName) {
        this.saveMockAnalytics(`fallback_${userName}`, {
          ...metadata,
          game_name: gameName,
          duration_seconds: durationSeconds,
          error: 'No patient_id available'
        });
      }
      return;
    }
    
    const clinicianId = metadata.clinicianId || user.clinicianId;

    console.log('📊 trackDetailedGameMetrics - IDs:', {
      patientId,
      patientIdType: typeof patientId,
      storedPatientId,
      storedType: typeof storedPatientId,
      metadataPatientId: metadata.patient_id,
      userObject: user,
      userName,
      sessionId: this.currentSessionId,
      metadata
    });

    // Calculate ROM display from percentage
    const romPercentage = metadata.rom || metadata.rangeOfMotion || 0;
    let romDisplay = 'Limited';
    if (romPercentage >= 90) romDisplay = 'Full range';
    else if (romPercentage >= 67) romDisplay = '¾ range';
    else if (romPercentage >= 45) romDisplay = '½ range';
    else if (romPercentage >= 22) romDisplay = '¼ range';

    // Ensure patient_id is stored as a string for consistency
    const detailedEvent = {
      patient_id: String(patientId), // Always store as string
      clinician_id: clinicianId,
      session_id: this.currentSessionId,
      game_name: gameName,
      exercise_type: metadata.exerciseType || 'Unknown',

      // Timing
      start_time: new Date(Date.now() - (durationSeconds || 0) * 1000).toISOString(),
      end_time: new Date().toISOString(),
      duration_seconds: durationSeconds,

      // Core metrics
      reps_completed: metadata.reps_completed || metadata.reps || 0,
      reps_target: metadata.reps_target || 10,
      form_score: metadata.formScore || metadata.form_score || 0,

      // ROM metrics
      rom_percentage: romPercentage,
      rom_display: romDisplay,
      rom_degrees: metadata.romDegrees,
      max_rom_achieved: metadata.maxRom || romPercentage,

      // Detailed form components
      accuracy_score: metadata.accuracyScore || metadata.accuracy || 0,
      control_score: metadata.controlScore || metadata.control || 0,
      symmetry_score: metadata.symmetryScore || metadata.symmetry || 0,
      velocity_score: metadata.velocityScore || metadata.velocity || 0,
      stability_score: metadata.stabilityScore || metadata.stability || 0,

      // Additional metrics
      detailed_metrics: metadata.detailedMetrics || {},
      completion_status: metadata.completionStatus || 'completed'
    };

    // For mock patients or when analytics is disabled, store in localStorage
    if (!this.isEnabled || String(patientId).startsWith('mock_')) {
      this.saveMockAnalytics(String(patientId), detailedEvent);
    } else {
      this.addEventToBuffer(detailedEvent, 'game_analytics');
      // Force immediate flush for game analytics to ensure they're saved
      setTimeout(() => this.flushEvents(), 100);
    }
    console.log('📊 Detailed game metrics tracked:', gameName, `Form: ${detailedEvent.form_score}%, ROM: ${romDisplay}`, 'Duration:', durationSeconds);

    // Update patient's last active timestamp
    if (patientId) {
      patientService.updatePatientLastActive(patientId);
    }
  }

  /**
   * Track platform session (overall engagement)
   */
  async trackPlatformSession(patientId, sessionData) {
    if (!this.isEnabled) return;

    try {
      const { data, error } = await supabase
        .from('platform_sessions')
        .insert([{
          patient_id: patientId,
          clinician_id: sessionData.clinicianId,
          start_time: sessionData.startTime,
          end_time: sessionData.endTime,
          total_duration_seconds: sessionData.totalDuration,
          active_duration_seconds: sessionData.activeDuration,
          games_played: sessionData.gamesPlayed,
          unique_games_played: sessionData.uniqueGames,
          total_reps: sessionData.totalReps,
          avg_form_score: sessionData.avgFormScore,
          session_quality: sessionData.quality || 'good',
          fatigue_level: sessionData.fatigueLevel || 'moderate'
        }])
        .select()
        .single();

      if (error) throw error;

      console.log('📊 Platform session tracked:', sessionData.totalDuration, 'seconds');
      return data;
    } catch (error) {
      console.error('Analytics: Failed to track platform session', error);
      return null;
    }
  }

  /**
   * Generate daily summary for a patient
   */
  async generateDailySummary(patientId, date = new Date()) {
    if (!this.isEnabled) return;

    try {
      const dateStr = date.toISOString().split('T')[0];

      // Call the database function to generate summary
      const { data, error } = await supabase
        .rpc('generate_daily_summary', {
          p_patient_id: patientId,
          p_date: dateStr
        });

      if (error) throw error;

      console.log('📊 Daily summary generated for patient:', patientId, 'date:', dateStr);
      return data;
    } catch (error) {
      console.error('Analytics: Failed to generate daily summary', error);
      return null;
    }
  }

  /**
   * Get current session info
   */
  getCurrentSession() {
    return {
      sessionId: this.currentSessionId,
      sessionStartTime: this.sessionStartTime,
      sessionDuration: this.sessionStartTime ?
        Math.floor((new Date() - this.sessionStartTime) / 1000) : 0
    };
  }

  /**
   * Manual flush for immediate processing
   */
  async flush() {
    return this.flushEvents();
  }

  /**
   * Check if analytics is enabled and working
   */
  isAnalyticsEnabled() {
    return this.isEnabled;
  }

  /**
   * Save mock analytics to localStorage
   */
  saveMockAnalytics(patientId, analyticsEvent) {
    try {
      const mockAnalyticsKey = `mock_analytics_${patientId}`;

      // Get existing analytics or create new structure
      let storedData = localStorage.getItem(mockAnalyticsKey);
      let analytics = storedData ? JSON.parse(storedData) : { sessions: [], summary: {} };

      // Add the new session
      analytics.sessions.push(analyticsEvent);

      // Keep only last 50 sessions
      if (analytics.sessions.length > 50) {
        analytics.sessions = analytics.sessions.slice(-50);
      }

      // Update summary
      analytics.summary = {
        total_sessions: analytics.sessions.length,
        total_reps: analytics.sessions.reduce((sum, s) => sum + (s.reps_completed || 0), 0),
        avg_form_score: analytics.sessions.length > 0
          ? analytics.sessions.reduce((sum, s) => sum + (s.form_score || 0), 0) / analytics.sessions.length
          : 0,
        avg_rom_percentage: analytics.sessions.length > 0
          ? analytics.sessions.reduce((sum, s) => sum + (s.rom_percentage || 0), 0) / analytics.sessions.length
          : 0
      };

      // Save back to localStorage
      localStorage.setItem(mockAnalyticsKey, JSON.stringify(analytics));
      console.log('📊 Mock analytics saved to localStorage for patient:', patientId);
    } catch (error) {
      console.error('📊 Failed to save mock analytics:', error);
    }
  }
}

// Create singleton instance
const analyticsService = new AnalyticsService();

// Export both the instance and the class for testing
export default analyticsService;
export { AnalyticsService };

// Expose for debugging in console
if (typeof window !== 'undefined') {
  window.analyticsService = analyticsService;
  window.flushAnalytics = () => {
    console.log('📊 Manually flushing analytics...');
    return analyticsService.flushEvents();
  };
  console.log('📊 Analytics debugging available: window.flushAnalytics()');
} 